import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, Gauge, ChevronDown } from "lucide-react";
import { TID } from "@/lib/testIds";
import pickRandom, { PKW_IMAGES } from "@/lib/randomImage";

const HERO_IMAGE = pickRandom(PKW_IMAGES);

const EASE = [0.76, 0, 0.24, 1];
const LINES = ["MEHR LEISTUNG.", "WENIGER VERBRAUCH.", "KOMPROMISSLOS PRÄZISE."];

const scrollTo = (sel) => {
  const el = document.querySelector(sel);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Hero({ portalUrl }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const yImg = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  // pointer tilt for the hero visual
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 15 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 15 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="hero-section"
      data-testid={TID.hero.root}
      ref={ref}
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16"
    >
      {/* backdrop */}
      <motion.div style={{ y: yBg }} className="absolute inset-0 vpt-grid" aria-hidden="true" />
      <div
        className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full blur-[130px] opacity-30"
        style={{ background: "radial-gradient(circle, #FFED00 0%, transparent 70%)" }}
        aria-hidden="true"
      />
      <div
        className="absolute bottom-0 right-0 h-[560px] w-[560px] rounded-full blur-[150px] opacity-25"
        style={{ background: "radial-gradient(circle, #FFEA00 0%, transparent 70%)" }}
        aria-hidden="true"
      />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 w-full grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex items-center gap-3 text-xs md:text-sm uppercase tracking-[0.35em] text-primary mb-7"
          >
            <span className="h-px w-10 bg-primary" />
            VPT Engineering — Automotive Software
          </motion.p>

          <h1 className="font-display font-bold tracking-tighter uppercase leading-[0.92] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl">
            {LINES.map((line, i) => (
              <span key={i} className="reveal-mask">
                <motion.span
                  className={`block ${i === 2 ? "text-glow-yellow text-primary" : "text-white"}`}
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, ease: EASE, delay: 0.25 + i * 0.12 }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-8 max-w-xl text-base md:text-lg text-white/60 leading-relaxed"
          >
            Individuelle ECU-Files für <span className="text-white">LKW</span>,{" "}
            <span className="text-white">Agrar-Nutzfahrzeuge</span> und{" "}
            <span className="text-white">PKW</span> — entwickelt von Technikern,
            individuell abgestimmt.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              data-testid={TID.hero.ctaPortal}
              href={portalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-display font-bold uppercase tracking-wider px-8 py-4 hover:bg-white transition-colors duration-300"
            >
              Zum Portal / Login
              <ArrowUpRight className="h-5 w-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
            <button
              data-testid={TID.hero.ctaKonfigurator}
              onClick={() => scrollTo("#konfigurator-section")}
              className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-display font-bold uppercase tracking-wider px-8 py-4 hover:border-accent hover:text-accent transition-colors duration-300"
            >
              <Gauge className="h-5 w-5" />
              Konfigurator öffnen
            </button>
          </motion.div>
        </div>

        {/* hero visual */}
        <motion.div
          style={{ y: yImg }}
          className="lg:col-span-5 hidden lg:block"
          onMouseMove={onMove}
          onMouseLeave={() => { mx.set(0); my.set(0); }}
        >
          <motion.div
            style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000 }}
            className="relative"
          >
            <div className="absolute -inset-2 border border-accent/30 translate-x-4 translate-y-4" aria-hidden="true" />
            <div className="relative overflow-hidden shadow-[0_0_60px_rgba(255,234,0,0.18)]">
              <img
                src={HERO_IMAGE}
                alt="Performance Fahrzeug am Prüfstand"
                className="w-full h-[460px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent" />
              <div className="absolute bottom-5 left-5 flex items-center gap-3">
                <span className="h-2 w-2 rounded-full bg-primary animate-vpt-pulse" />
                <span className="font-display uppercase tracking-widest text-sm text-white">Individuell abgestimmt</span>
              </div>
            </div>
            <div className="absolute -top-6 -right-6 bg-primary text-primary-foreground px-5 py-3 font-display font-bold uppercase tracking-wider text-sm">
              +30% Leistung
            </div>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("#stats-section")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 hover:text-primary transition-colors"
        aria-label="Nach unten scrollen"
      >
        <ChevronDown className="h-7 w-7 animate-bounce" />
      </motion.button>
    </section>
  );
}
